import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography, Avatar, InputAdornment, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Popup from 'reactjs-popup';
import { Calendar } from '@hassanmojab/react-modern-calendar-datepicker';
import { utils } from 'react-modern-calendar-datepicker';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { GeneralContainer, H1, Table, Text } from '../../components/UI/generalLayout/Layout.styled';
import ScreenHeader from '../../components/UI/screenHeader/ScreenHeader';
import { CalendarAnchor, CalendarRowContainer } from '../appointmentPanel/AppointmentPanel.styled';
import CareLineDropdown from '../../components/dropdown/CareLineDropdown';
import { GenericSolidButton } from '../../components/UI/buttons/Button.styled';
import Colors from '../../constants/Colors';
import { NavegacaoService } from '../../core/http/service/NavegacaoService';
import { PersonService } from '../../core/http/service/PersonService';
import { meses, ptBrCustomLocale } from '../../constants/Objects';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';

const PatientAvatar = ({ foto, name }: { foto: string | null; name: string }) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (foto) {
      const service = new PersonService();
      service.getPhoto(foto)
        .then((res: any) => {
          if (!active) return;
          const blob = new Blob([res.data], { type: res.headers["content-type"] });
          const image = URL.createObjectURL(blob);
          setPhotoUrl(image);
        })
        .catch((err) => {
          console.error('Error loading patient avatar photo:', err);
        });
    }
    return () => {
      active = false;
    };
  }, [foto]);

  if (photoUrl) {
    return <Avatar src={photoUrl} style={{ width: 36, height: 36 }} />;
  }

  return (
    <Avatar style={{ backgroundColor: Colors.SPM600, fontSize: '1rem', width: 36, height: 36 }}>
      {name.charAt(0)}
    </Avatar>
  );
};

const PainelNavegacao = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: any = location?.state;
  const { isMobile } = useResponsiveInfo();

  // Load units from Redux
  const { units, unitSelectedAppointments } = useSelector((state: any) => state.unit);

  const [selectedUnit, setSelectedUnit] = useState<any>(
    unitSelectedAppointments || { id: '', nome: '' }
  );

  const [selectedDay, setSelectedDay] = useState<any>(utils('en').getToday());
  const [searchText, setSearchText] = useState<string>(locationState?.searchText || '');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(locationState?.searchText || '');
  const [targetPacienteId, setTargetPacienteId] = useState<string | undefined>(locationState?.pacienteId);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [patients, setPatients] = useState<any[]>([]);

  useEffect(() => {
    if (locationState?.searchText || locationState?.pacienteId) {
      setSearchText(locationState.searchText || '');
      setDebouncedSearchText(locationState.searchText || '');
      setTargetPacienteId(locationState.pacienteId);
    }
  }, [locationState?.searchText, locationState?.pacienteId]);

  // Update selected unit when Redux state changes
  useEffect(() => {
    if (unitSelectedAppointments) {
      setSelectedUnit(unitSelectedAppointments);
    }
  }, [unitSelectedAppointments]);

  // Debounce search text
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchText]);

  const loadData = useCallback(async () => {
    if (!selectedUnit?.id || !selectedDay) return;

    setIsLoading(true);
    const formattedDate = `${selectedDay.year}-${selectedDay.month}-${selectedDay.day}`;
    try {
      const service = new NavegacaoService();
      const res: any = await service.getPainel(selectedUnit.id, formattedDate, debouncedSearchText, targetPacienteId);
      setPatients(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      setPatients([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedUnit, selectedDay, debouncedSearchText, targetPacienteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeUnits = (units || []).filter((u: any) => !u.inativo);

  const formatSelectedDay = () => {
    return `${selectedDay.day} de ${meses[selectedDay.month - 1]} de ${selectedDay.year}`;
  };

  const PopupCalendar = () => {
    return (
      <Popup
        position="bottom center"
        nested
        trigger={
          <CalendarAnchor isMobile={isMobile} style={{ marginLeft: 0 }}>
            {formatSelectedDay()}
            <KeyboardArrowDownIcon />
          </CalendarAnchor>
        }
      >
        <Calendar
          value={selectedDay}
          onChange={setSelectedDay}
          locale={ptBrCustomLocale}
          colorPrimary={Colors.SPM600}
          colorPrimaryLight={Colors.SPM50}
          shouldHighlightWeekends
        />
      </Popup>
    );
  };

  const renderAltaBadge = (p: any) => {
    const isAlta = !p.ativo || !!p.dataAlta || !!p.motivoAlta;

    if (isAlta) {
      const motivoText = p.motivoAlta || 'Não informado';
      const dataText = p.dataAlta ? ` em ${p.dataAlta}` : '';
      const tooltipTitle = `Motivo da Alta: ${motivoText}${dataText}`;

      const details = [p.dataAlta, p.motivoAlta].filter(Boolean).join(' - ');
      return (
        <Tooltip title={tooltipTitle} arrow placement="top">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              backgroundColor: '#fce4ec',
              color: '#c2185b',
              border: '1px solid #f8bbd0',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.3px',
              cursor: 'pointer',
            }}
          >
            <span>● Alta</span>
          </div>
        </Tooltip>
      );
    }

    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          backgroundColor: '#e0f2f1',
          color: '#00695c',
          border: '1px solid #b2dfdb',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '0.78rem',
          fontWeight: 700,
          letterSpacing: '0.3px',
        }}
      >
        <span>● Em Navegação</span>
      </div>
    );
  };

  const renderStatusBadge = (dataAprazamento?: string) => {
    if (!dataAprazamento) return null;

    const todayStr = new Date().toISOString().split('T')[0];
    const [year, month, day] = dataAprazamento.split('-');
    const formattedDate = `${day}/${month}/${year}`;

    if (dataAprazamento < todayStr) {
      return (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            backgroundColor: '#fff3e0',
            color: '#e65100',
            border: '1px solid #ffe0b2',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
          }}
        >
          <span>● Passado</span>
          <span style={{ fontWeight: 500, color: '#bf360c' }}>({formattedDate})</span>
        </div>
      );
    } else if (dataAprazamento > todayStr) {
      return (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            backgroundColor: '#e8eaf6',
            color: '#1a237e',
            border: '1px solid #c5cae9',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
          }}
        >
          <span>● Futuro</span>
          <span style={{ fontWeight: 500, color: '#283593' }}>({formattedDate})</span>
        </div>
      );
    } else {
      return (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            backgroundColor: '#e8f5e9',
            color: '#1b5e20',
            border: '1px solid #c8e6c9',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
          }}
        >
          <span>● Hoje</span>
          <span style={{ fontWeight: 500, color: '#2e7d32' }}>({formattedDate})</span>
        </div>
      );
    }
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <ScreenHeader
          screenTitle={"Painel de Navegação"}
          hasRightButton={false}
          rightButtonText=""
          onRightButtonClick={() => {}}
        />

        {/* Existing Application Style Inline Filters (Mobile Stacked, Desktop Inline) */}
        <div style={{ display: 'flex', gap: isMobile ? 15 : 30, alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 5 : 10, width: isMobile ? '100%' : 'auto' }}>
            <Text style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem', margin: 0, whiteSpace: 'nowrap' }}>Unidade: </Text>
            <CareLineDropdown
              placeholder="Selecione a Unidade de Atendimento"
              style={{ width: isMobile ? '100%' : 310 }}
              value={selectedUnit}
              onChange={setSelectedUnit}
              items={activeUnits}
              disabled={false}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 5 : 10, width: isMobile ? '100%' : 'auto' }}>
            <Text style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem', margin: 0, whiteSpace: 'nowrap' }}>Agendas de </Text>
            <CalendarRowContainer isMobile={isMobile}>
              <PopupCalendar />
            </CalendarRowContainer>
          </div>
        </div>

        {/* Second Line Filters (Search Input & Refresh Button) */}
        <div style={{ display: 'flex', gap: 15, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 25 }}>
          <TextField
            placeholder="Busca por nome do paciente ou profissional"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setTargetPacienteId(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                loadData();
              }
            }}
            variant="outlined"
            size="small"
            style={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#aaa' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fcfcfc',
              }
            }}
          />
          <GenericSolidButton
            onClick={loadData}
            style={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '0 20px',
              border: `1px solid ${Colors.SPM600}`,
              color: Colors.SPM600,
              backgroundColor: 'white',
              borderRadius: 8,
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <RefreshIcon style={{ fontSize: '1.2rem' }} /> Atualizar
          </GenericSolidButton>
        </div>

        {/* Patients Table List */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress color="primary" />
          </Box>
        ) : patients.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <Table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', fontSize: '0.9rem' }}>Nome</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', fontSize: '0.9rem' }}>Alta</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', fontSize: '0.9rem' }}>Situação</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', fontSize: '0.9rem' }}>Turno</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', fontSize: '0.9rem' }}>Profissional</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', width: 150 }}></th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => (
                  <tr key={p.navegacaoId} style={{ borderBottom: '1px solid #e0e0e0', verticalAlign: 'middle' }}>
                    <td style={{ padding: '16px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <PatientAvatar foto={p.pacienteFoto} name={p.pacienteNome} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>{p.pacienteNome}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 16px' }}>
                      {renderAltaBadge(p)}
                    </td>
                    <td style={{ padding: '16px 16px' }}>
                      {renderStatusBadge(p.dataAprazamento)}
                    </td>
                    <td style={{ padding: '16px 16px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: '#e3f2fd', color: '#0d47a1', padding: '4px 12px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600 }}>
                        <AccessTimeIcon style={{ fontSize: '1rem', color: '#0d47a1' }} />
                        {p.turno}
                      </div>
                    </td>
                    <td style={{ padding: '16px 16px', color: '#555', fontSize: '0.9rem' }}>
                      {p.profissionalInclusaoNome}
                    </td>
                    <td style={{ padding: '16px 16px', textAlign: 'right' }}>
                      <span
                        onClick={() => navigate(`/acompanhamentoNavegacao/${p.navegacaoId}`)}
                        style={{
                          color: Colors.SPM600,
                          fontWeight: 'bold',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Navegação <ChevronRightIcon style={{ fontSize: '1.2rem', color: Colors.SPM600 }} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
            <Typography variant="body1" color="textSecondary" style={{ fontStyle: 'italic' }}>
              Nenhum paciente em processo de navegação para os filtros selecionados.
            </Typography>
          </Box>
        )}
      </GeneralContainer>
    </ContainerWithSidebar>
  );
};

export default PainelNavegacao;
